import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetSingleLessonQuery } from '../../../../reduxToolkit/reducers/usersSlice';
import { use, useEffect, useRef, useState } from 'react';
import Loader from '../../../../layout/Loader';
import { useAppSelector } from '../../../../reduxToolkit/hooks';
import {
  Col,
  Card,
  CardBody,
  Container,
  Row,
  Button,
  Modal,
  Alert,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import { Image } from '../../../../coreComponents/Image';
import CommonCardHeader from '../../../../coreComponents/CommonCardHeader';
import Breadcrumbs from '../../../../coreComponents/breadcrumbs';
import ReactPlayer from 'react-player';
import SimpleModal from '../../../uiKits/modal/basicModal/SimpleModal';

import TimePickerWithLimitedTime from '../../../formsAndTable/forms/formWidgets/datePicker/timePicker/TimePickerWithLimitedTime';
import { bookStudentExam } from '../../../../services/api';
import { useTranslation } from 'react-i18next';
import { AdditionalMaterial, CourseLessonTranslations } from '../../../../types/Tables';
import i18n from '../../../../i18n';
import VideoJSPlayer from '../../../videoPlayer/VideoJSPlayer';
import { LessonVideo, StudentCourseLesson } from '../../../../types/Courses';
import { bindVideoTracking, capture } from '../../../../services/analytics';
import { baseLang, getCalendarStops } from '../../../../lib/utils';
import Swal from 'sweetalert2';
import { hideSwalLoader, showSwalLoader } from '../../../bonusUi/sweetAlert/Loader';

import { sortPlaylist } from '../../../videoPlayer/playlistHelper';

const isExamBookingDisabled = (studentId: string | null) => {
  const disabledFrom = process.env.REACT_APP_EXAM_BOOKING_DISABLED_FROM;
  if (!studentId || !disabledFrom) return false;

  const exemptIds = (process.env.REACT_APP_PAUSE_NOTICE_EXEMPT_USER_ID || '')
    .split(',')
    .map((id) => id.trim());
  if (exemptIds.includes(String(studentId))) return false;

  return Date.now() >= new Date(`${disabledFrom}T00:00:00`).getTime();
};

const StudentLessonDetails = () => {
  const { t } = useTranslation();
  const { id: lesson_id } = useParams<{ id: string }>();
  const { id: studentId, role } = useAppSelector((state) => state.auth);
  const [showPrivate, setShowPrivate] = useState(false);
  const { data, isLoading, isSuccess, status, error } = useGetSingleLessonQuery({
    studentId,
    role,
    lesson_id,
  });
  const [isOpen, setIsOpen] = useState(false);
  const bookingDisabled = isExamBookingDisabled(studentId);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookDate, setBookDate] = useState(new Date());

  const [examBookData, setExamBookData] = useState<{
    studentId: string | undefined;
    lessonId: number | undefined;
    bookDate: Date | undefined;
  }>({
    studentId: studentId ?? undefined,
    lessonId: Number(lesson_id),
    bookDate: undefined,
  });

  const [lessonTranslation, setLessonTranslation] = useState<{
    lessonTitle: string;
    lessonDescription: string;
    courseName: string;
    nextLessonTitle: string;
    prevLessonTitle: string;
  }>({
    lessonTitle: '',
    lessonDescription: '',
    courseName: '',
    nextLessonTitle: '',
    prevLessonTitle: '',
  });
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; link: string }[]>([]);
  const [langSelected, setLangSelected] = useState<'uk' | 'ru' | null>(null);
  const [calendarStopPeriods, setCalendarStopPeriods] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const cleanupRef = useRef<() => void>(() => {});

  const [pickerRefreshKey, setPickerRefreshKey] = useState(0);

  const pageLang = baseLang(i18n.language);
  const [lastTriedSelection, setLastTriedSelection] = useState<Date | null>(null);
  const [preferExactSelection, setPreferExactSelection] = useState(false);
  const [playlist, setPlaylist] = useState<LessonVideo[]>([]);
  const [currentVideo, setCurrentVideo] = useState<LessonVideo | null>(null);
  const router = useNavigate();
  useEffect(() => () => cleanupRef.current?.(), []);

  useEffect(() => {
    getCalendarStops(setCalendarStopPeriods);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      getCalendarStops(setCalendarStopPeriods);
      setPickerRefreshKey((k) => k + 1);
    })();
  }, [isOpen]);

  const handleCancelBook = () => {
    setBookingError(null);
    setExamBookData({
      studentId: studentId ?? undefined,
      lessonId: Number(lesson_id),
      bookDate: undefined,
    });
    setLangSelected(null);
    setBookDate(new Date());
    setIsOpen(false);
    setPreferExactSelection(false);
    setLastTriedSelection(null);
  };

  const handleExamBook = async (selectedDate: Date | null) => {
    if (!selectedDate || !langSelected || bookingDisabled) {
      return;
    }
    setLastTriedSelection(selectedDate);
    setBookingError(null);
    const payload = {
      studentId: studentId ?? undefined,
      lessonId: Number(lesson_id),
      bookDate: selectedDate ?? undefined,
      examLang: langSelected,
    };

    try {
      if (isExamBookingDisabled(studentId)) {
        handleCancelBook();
        return;
      }
      showSwalLoader(t('exams.bookingExam'));
      const resp = await bookStudentExam(payload); // ✅ always up-to-date
      if (resp?.dateTaken) {
        setPreferExactSelection(true);
        hideSwalLoader();
        setTimeout(() => {
          Swal.fire({
            title: t('courses.exam.calendar.dateTaken'),
            icon: 'warning',
            confirmButtonText: t('global.ok'),
          });
        }, 1000);
        await getCalendarStops(setCalendarStopPeriods);
      } else if (resp?.dateClosed) {
        setPreferExactSelection(true);
        hideSwalLoader();
        // setConfirmation(10);
        setTimeout(() => {
          Swal.fire({
            title: t('courses.exam.calendar.dateClosed'),
            icon: 'warning',
            confirmButtonText: t('global.ok'),
          });
        }, 1000);
        await getCalendarStops(setCalendarStopPeriods);
      } else if (resp.dateIsToday) {
        setPreferExactSelection(true);
        hideSwalLoader();
        setTimeout(() => {
          Swal.fire({
            title: t('courses.exam.calendar.dateIsToday'),
            icon: 'warning',
            confirmButtonText: t('global.ok'),
          });
        }, 1000);
        await getCalendarStops(setCalendarStopPeriods);
      } else if (resp) {
        setPreferExactSelection(false);
        setLastTriedSelection(null);
        hideSwalLoader();
        // setConfirmation(10);
        setIsOpen(false);
        capture('exam_registered', { ...payload });
        setTimeout(() => {
          Swal.fire({
            title: t('exams.bookingSuccessTitle'),
            text: t('exams.bookingSuccessText'),
            icon: 'success',
            confirmButtonText: t('global.ok'),
          });
        }, 1000);
      }
    } catch (error) {
      hideSwalLoader();
      const response = (error as {
        response?: { status?: number; data?: { message?: string } };
      }).response;
      let message = t('exams.bookingFailed');
      if (response?.status === 403 && response.data?.message === 'Exam booking is suspended') {
        message = t('exams.bookingSuspended');
      }
      setBookingError(message);
    } finally {
      // setShowConfirm(true);
    }
  };
  const mapToDbLang = (lng?: string) => {
    const l = (lng || '').toLowerCase();
    if (l.startsWith('uk')) return 'uk';
    if (l.startsWith('ru')) return 'ru';
    return 'base';
  };
  const getTranslate = (lng: string, lessonData: StudentCourseLesson) => {
    const lang = mapToDbLang(lng);
    if (lang === 'base') {
      const translation = {
        lessonTitle: lessonData?.title,
        lessonDescription: lessonData?.description ?? '',
        courseName: lessonData.course?.name,
        nextLessonTitle: lessonData.nextLesson?.title || '',
        prevLessonTitle: lessonData.prevLesson?.title || '',
      };
      setLessonTranslation(translation);
      setBreadcrumbs([
        {
          name: t('sidebar.myCourses'),
          link: '/courses/my-courses',
        },
        {
          name: lessonData.course.name,
          link: `/courses/my-courses/details/${lessonData.course.id}`,
        },
      ]);
    } else {
      const lessonTranslation = lessonData?.translations?.find(
        (el: CourseLessonTranslations) => el.lang === lang,
      );
      const courseTranslation = lessonData?.course?.translations?.find(
        (el: CourseLessonTranslations) => el.lang === lang,
      );

      const prevLessonTranslation = lessonData?.prevLesson?.translations?.find(
        (el: CourseLessonTranslations) => el.lang === lang,
      );

      const nextLessonTranslation = lessonData?.nextLesson?.translations?.find(
        (el: CourseLessonTranslations) => el.lang === lang,
      );
      const translation = {
        lessonTitle: lessonTranslation?.title ?? lessonData?.title ?? '',
        lessonDescription: lessonTranslation?.description ?? lessonData?.description ?? '',
        courseName: courseTranslation?.name ?? lessonData?.course?.name ?? '',
        nextLessonTitle: nextLessonTranslation?.title || '',
        prevLessonTitle: prevLessonTranslation?.title || '',
      };
      setLessonTranslation(translation);
      setBreadcrumbs([
        {
          name: t('sidebar.myCourses'),
          link: '/courses/my-courses',
        },
        {
          name: translation.courseName,
          link: `/courses/my-courses/details/${data?.lesson?.course?.id}`,
        },
      ]);
    }
  };
  useEffect(() => {
    if (!isSuccess || status !== 'fulfilled') return;
    const lesson = data?.lesson;
    if (!lesson) return;
    if (data && data?.lesson?.video.length > 0) {
      const videoList = data.lesson.video;
      const sortedPlaylist = videoList.length > 0 ? sortPlaylist(videoList) : [];
      setPlaylist(sortedPlaylist);
      setCurrentVideo(sortedPlaylist.length > 0 ? sortedPlaylist[0] : null);

    }

    getTranslate(pageLang, data.lesson);
  }, [data, isSuccess, status, i18n, i18n.language]);

  const prevLessonId = data?.lesson?.prevLesson?.id ?? null;

  const nextLessonId = data?.lesson?.nextLesson?.id ?? null;

  useEffect(() => {
    console.log('Data lesson changed:', data?.lesson);
    console.log('prev lesson:', data?.lesson?.prevLesson?.id);
    console.log('next lesson:', data?.lesson?.nextLesson?.id);
  }, [data]);

  const handleBackLink = () => {
    const backLink = location.state?.from || `/courses/my-courses/details/${data.lesson.course.id}`;
    // window.history.pushState(null, '', backLink);
    // window.location.href = backLink;
    navigate(backLink, {
      state: { restoreY: location.state?.scrollY ?? 0 },
      replace: false,
    });
  };
  const getCurrentVideoLocalizedTitle = () => {
    if (!currentVideo) return '';
    switch (pageLang) {
      case 'en':
        return currentVideo?.filename_en ?? currentVideo.filename;
      case 'uk':
        return currentVideo?.filename_uk ?? currentVideo.filename;
      case 'ru':
        return currentVideo?.filename_ru ?? currentVideo.filename;
      default:
        return currentVideo.filename;
    }
  };
  return (
    <>
      {isLoading && <Loader />}
      {isSuccess && status === 'fulfilled' && (
        <>
          {' '}
          <Breadcrumbs
            mainTitle={lessonTranslation.lessonTitle}
            // parent={t('courses.generalTitle')}
            parentsArray={breadcrumbs}
          />
          <Container fluid>
            <Row>
              <Col xl="9" className="xl-60 order-xl-0 order-1 box-col-12 ">
                <Card>
                  {/* <CommonCardHeader title={lessonTranslation.lessonTitle} tag tagClass="pb-4" /> */}
                  <CardBody>
                    <Row>
                      <Col sm="12" className="d-flex justify-content-between border-bottom mb-3">
                        <Col xs="4" className="mb-3">
                          {' '}
                          <Button color="primary" outline onClick={handleBackLink}>
                            {t('global.backToCourse')}
                          </Button>
                        </Col>
                        <Col xs="auto" className="mb-3 ms-auto d-flex gap-2">
                          <Button
                            color="primary"
                            outline
                            disabled={!prevLessonId}
                            onClick={() =>
                              prevLessonId && router(`/courses/my-courses/lesson/${prevLessonId}`)
                            }
                          >
                            <i className="fa fa-fast-backward"></i>
                          </Button>

                          <Button
                            color="primary"
                            outline
                            disabled={!nextLessonId}
                            onClick={() =>
                              nextLessonId && router(`/courses/my-courses/lesson/${nextLessonId}`)
                            }
                          >
                            <i className="fa fa-fast-forward"></i>
                          </Button>
                        </Col>
                      </Col>
                      <Col
                        sm="12"
                        className="d-flex justify-content-between align-items-center mb-3"
                      >
                        <Col xs="8">
                          <h3 className="text-truncate">
                            {' '}
                            {t('courses.lessons.detailsPage.video', {
                              videoTitle: getCurrentVideoLocalizedTitle(),
                            })}
                          </h3>
                          <p>
                            {' '}
                            {t('video.playsVideo', {
                              current:
                                playlist.findIndex(
                                  (video) => video.playlist_pos === currentVideo?.playlist_pos,
                                ) + 1,
                              total: playlist.length,
                            })}
                          </p>
                        </Col>

                        {playlist?.length > 1 && (
                          <Col xs="auto" className="d-flex gap-2">
                            <Button
                              color="primary"
                              outline
                              disabled={currentVideo?.id === playlist[0]?.id} // Дизейблимо, якщо це перше відео
                              onClick={() => {
                                const currentIndex = playlist.findIndex(
                                  (video) => video.playlist_pos === currentVideo?.playlist_pos,
                                );
                                const prevIndex =
                                  (currentIndex - 1 + playlist.length) % playlist.length;
                                setCurrentVideo(playlist[prevIndex]);
                              }}
                            >
                              <i className="fa fa-step-backward"></i>
                            </Button>

                            <Button
                              color="primary"
                              outline
                              disabled={currentVideo?.id === playlist[playlist.length - 1]?.id} // Дизейблимо, якщо це останнє відео
                              onClick={() => {
                                const currentIndex = playlist.findIndex(
                                  (video) => video.playlist_pos === currentVideo?.playlist_pos,
                                );
                                const nextIndex = (currentIndex + 1) % playlist.length;
                                setCurrentVideo(playlist[nextIndex]);
                              }}
                            >
                              <i className="fa fa-step-forward"></i>
                            </Button>
                          </Col>
                        )}
                      </Col>
                    </Row>
                    <div className="blog-single">
                      <div className="blog-box blog-details">
                        {data.lesson.video ? (
                          <div className="ratio ratio-16x9 mb-4">
                            <VideoJSPlayer
                              ref={videoElRef}
                              src={
                                currentVideo?.link
                                  ? currentVideo.link
                                  : `${process.env.REACT_APP_BACKEND_API}/video/file/${currentVideo?.signedSrc}`
                              }
                              onReady={({ videoEl }) => {
                                cleanupRef.current?.();
                                cleanupRef.current = bindVideoTracking(
                                  videoEl,
                                  String(currentVideo?.id),
                                  String(data.lesson.course.id),
                                  String(data.lesson.id),
                                  data.lesson.title,
                                  data.lesson.course.name,
                                );
                              }}
                            />
                          </div>
                        ) : (
                          <Image
                            className="img-fluid sm-100-w"
                            src={`${process.env.REACT_APP_BACKEND_STATIC}/coursesPics/${data.lesson.lessonCover}`}
                            alt=""
                          />
                        )}
                        <div className="blog-details pb-0">
                          {showPrivate && (
                            <ul className="blog-social">
                              <li>
                                {t('courses.lessons.enrollDate')}:{' '}
                                {data.createdAt
                                  ? new Date(data.createdAt).toLocaleDateString('uk-UA', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </li>
                              <li
                                className={`${data.status === 'completed' ? 'text-success' : 'text-warning'}`}
                              >
                                {t('courses.lessons.lessonStatus')}:{' '}
                                {data.status === 'completed'
                                  ? t('courses.courseStudyStatus.completed')
                                  : t('courses.courseStudyStatus.inProgress')}
                              </li>
                              <li>
                                {t('courses.lessons.lessonScore', {
                                  score: data.score ?? 0,
                                })}
                              </li>
                            </ul>
                          )}
                          <h4 className="pt-2">{t('courses.lessons.lessonSubHeading')}</h4>

                          {/* <Playlist
                            playlist={playlist}
                            currentVideo={currentVideo}
                            onVideoSelect={(video) => setCurrentVideo(video)}
                          /> */}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col xl="3" className="xl-40 order-xl-0 order-2 box-col-12 ">
                {/* {showPrivate && ( */}
                <Card>
                  {' '}
                  <CommonCardHeader
                    title={t('courses.lessons.lessonScoring')}
                    tag
                    headClass="pb-0"
                  />
                  <CardBody>
                    <Row className="align-items-center justify-content-center gap-2">
                      <Col xs="auto">
                        <Image
                          className="img-fluid"
                          style={{ maxWidth: '150px' }}
                          src={`${process.env.REACT_APP_BACKEND_STATIC}/coursesPics/${data.lesson.lessonCover}`}
                          alt=""
                        />
                      </Col>
                      {/* {data.lesson.video.length / 60 > 10 && ( */}
                      {!data.lesson.nonScore && (
                        <Col>
                          <div className="d-flex flex-column align-items-center gap-2 ">
                            <Button
                              disabled={bookingDisabled}
                              onClick={() => {
                                capture('open_exam_booking');
                                setIsOpen(true);
                              }}
                            >
                              {t('courses.exam.examBookExamBtn')}
                            </Button>

                            {/* {data.score && data.score.attempts > 0 ? (
                              <Card className="mt-3">
                                <CardBody>
                                  <p className="">
                                    {t('courses.exam.examBestScore', {
                                      score: data.score.maxScore ?? 0,
                                    })}
                                  </p>
                                  <p>
                                    {' '}
                                    {t('courses.exam.attempts', {
                                      attempts: data.score.attempts,
                                    })}
                                  </p>
                                </CardBody>
                              </Card>
                            ) : (
                              <Card>
                                <CardBody>
                                  <div className="d-flex align-items-center">
                                    <p className="text-center">
                                      {t('courses.exam.examTakeScoring')}
                                      <i className="icofont icofont-arrow-up"></i>
                                    </p>
                                  </div>
                                </CardBody>
                              </Card>
                            )} */}
                            <Card>
                              <CardBody>
                                <div className="d-flex align-items-center">
                                  <p className="text-center">
                                    {t('courses.exam.examTakeScoring')}
                                    <i className="icofont icofont-arrow-up"></i>
                                  </p>
                                </div>
                              </CardBody>
                            </Card>
                          </div>
                        </Col>
                      )}
                      {/* )} */}
                    </Row>
                  </CardBody>
                </Card>
                {/* )} */}
                <Card>
                  {' '}
                  <CommonCardHeader
                    title={t('courses.lessons.lessonDescription')}
                    tag
                    headClass="pb-0"
                  />
                  <CardBody>
                    <div>
                      {lessonTranslation.lessonDescription ??
                        t('courses.lessons.lessonNoDescription')}
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CommonCardHeader
                    title={t('courses.lessons.additionalMaterials.title')}
                    tag
                    tagClass="text-unclamp"
                    headClass="pb-0"
                  />
                  <CardBody>
                    {data.lesson.additionalMaterials &&
                      data.lesson.additionalMaterials.length > 0 && (
                        <ListGroup>
                          {data.lesson.additionalMaterials.map(
                            (item: AdditionalMaterial, index: number) => {
                              const materialsTitleEn = item.title;
                              const translatedTitle =
                                pageLang === 'uk' ? item.title_uk : item.title_ru;

                              return (
                                <ListGroupItem
                                  key={index} // ideally use item.id if you have it
                                  className="d-flex justify-content-between align-items-center mb-2"
                                >
                                  <div>
                                    <strong>
                                      {pageLang === 'en' ? materialsTitleEn : translatedTitle}
                                    </strong>{' '}
                                    ({item.kind})
                                  </div>
                                  <div>
                                    <a
                                      href={
                                        item.kind === 'file'
                                          ? `${process.env.REACT_APP_BACKEND_STATIC}${item.url}`
                                          : item.url
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {t('courses.lessons.additionalMaterials.view')}
                                    </a>
                                  </div>
                                </ListGroupItem>
                              );
                            },
                          )}
                        </ListGroup>
                      )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
          <SimpleModal size="lg" isOpen={isOpen} close={handleCancelBook}>
            {/* <Card className="m-4 "> */}
            <ModalHeader>
              <CommonCardHeader title={t('courses.exam.scoreBooking')} tag tagClass="pb-2" />
            </ModalHeader>
            <Alert color="alert alert-light-warning">
              <h5 className={`txt-dark`}>{t('courses.exam.examBookingAlert')}</h5>
            </Alert>
            <ModalBody>
              {bookingError && <Alert color="danger" role="alert">{bookingError}</Alert>}
              <Row className="mb-3 align-items-center">
                <Col md="4">
                  <p className="m-0">
                    <strong>{t('courses.generalCourseTitle')}:</strong>
                  </p>
                </Col>
                <Col md="6">{lessonTranslation.courseName}</Col>
              </Row>

              <Row className="mb-3 align-items-center">
                <Col md="4">
                  <p className="m-0">
                    <strong>{t('courses.generalLessonName')}:</strong>
                  </p>
                </Col>
                <Col md="8">{lessonTranslation.lessonTitle}</Col>
              </Row>

              <Row className="mb-3 align-items-center">
                <Col md="4">
                  <p className="m-0">
                    <strong>{t('courses.exam.examSelectDateTime')}:</strong>
                  </p>
                </Col>
                <Col md="8">
                  <TimePickerWithLimitedTime
                    setDateTime={(date) => setBookDate(date ?? new Date())}
                    start={1}
                    disabledPeriods={calendarStopPeriods}
                    initialSelection={lastTriedSelection} // <-- shows the wrong pick as default
                    preferExactSelection={preferExactSelection}
                    refreshKey={pickerRefreshKey} // you already have this
                  />
                </Col>
              </Row>
              <Row className="mb-3 align-items-center">
                <Col md="4">
                  <p className="m-0">
                    <strong>{t('courses.exam.examLang')}:</strong>
                  </p>
                </Col>
                <Col md="6">
                  {' '}
                  <FormGroup check>
                    <Input
                      type="radio"
                      name={`exam-lang`}
                      id={`exam-lang-uk`}
                      required
                      onChange={() => setLangSelected('uk')}
                      checked={langSelected === 'uk'}
                    />
                    <Label htmlFor={`exam-lang-uk`} check>
                      {t('courses.exam.examLangUkrainian')}
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Input
                      type="radio"
                      name={`exam-lang`}
                      id={`exam-lang-ru`}
                      required
                      onChange={() => setLangSelected('ru')}
                      checked={langSelected === 'ru'}
                    />
                    <Label htmlFor={`ru`} check>
                      {t('courses.exam.examLangRussian')}
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="d-flex justify-content-between">
                <Col xs="auto">
                  <Button
                    color="success"
                    disabled={bookingDisabled || !bookDate || !langSelected}
                    onClick={() => handleExamBook(bookDate)}
                  >
                    {t('courses.exam.examBookExamBtn')}
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button color="danger" onClick={handleCancelBook}>
                    {t('global.cancel')}
                  </Button>
                </Col>
              </Row>
            </ModalBody>
            {/* </Card> */}
          </SimpleModal>
          {/* <SimpleModal isOpen={showConfirm} close={() => setShowConfirm(false)}>
            <Confirmation keyValue={confirmation} close={() => setShowConfirm(false)} />
          </SimpleModal> */}
        </>
      )}
    </>
  );
};
export default StudentLessonDetails;
